import {LayerAttributeVO, LayerVO} from '../api';
import {LayerId} from '@mc/react-mapio';

/**
 * Vrátí náhodné celé číslo mezi hranicemi.
 *
 * @param {number} min - dolní hraníce (včetně)
 * @param {number} max - horní hranice (vyjma)
 * @return {number} náhodné číslo
 */
export const randomInt = (min: number, max: number): number => {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min) + min);
};

/**
 * Získání konfigurace vrstvy podle layerId
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID vrstvy jako číslo
 * @returns konfigurace vrstvy nebo undefined
 */
export const getLayerConfig = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): LayerVO | undefined => {
    if (!layersConfig || !layerId) return undefined;
    return layersConfig[layerId.toString()];
};

/**
 * Kontrola zda je vrstva user_db
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID vrstvy jako číslo
 * @returns true pokud je vrstva user_db
 */
export const isUserDbLayer = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): boolean => {
    const layerConfig = getLayerConfig(layersConfig, layerId);
    return layerConfig?.konektor === 'user_db';
};

/**
 * Kontrola zda má vrstva parentLayerId (diskriminátor)
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID vrstvy jako číslo
 * @returns true pokud má vrstva parentLayerId
 */
export const hasParentLayer = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): boolean => {
    const layerConfig = getLayerConfig(layersConfig, layerId);
    return Boolean(layerConfig?.parentLayerId);
};

/**
 * Získání parent layerId pokud existuje
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID vrstvy jako číslo
 * @returns parent layerId jako číslo nebo null
 */
export const getParentLayerId = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): number | null => {
    const layerConfig = getLayerConfig(layersConfig, layerId);
    return layerConfig?.parentLayerId ? Number(layerConfig.parentLayerId) : null;
};

/**
 * Vrátí číselná ID WMS/WMTS vrstev z daného seznamu vrstev. Legenda se pro tyto vrstvy
 * načítá z capabilities služby (na rozdíl od ostatních vrstev), proto je potřeba je vyfiltrovat.
 * @param layersConfig - konfigurace všech vrstev
 * @param layerIds - pole ID vrstev
 * @returns pole číselných ID WMS/WMTS vrstev
 */
export const getWmsWmtsLayerIds = (layersConfig: {[key: string]: LayerVO} | undefined, layerIds: LayerId[]): number[] => {
    if (!layersConfig) return [];
    return layerIds
        .map(id => layersConfig[id.toString()])
        .filter((layer): layer is LayerVO => !!layer && (layer.type === 'wms' || layer.type === 'wmts'))
        .map(layer => layer.id!);
};

/**
 * Filtrace vrstev podle konektoru
 * @param layersConfig - konfigurace všech vrstev
 * @param layerIds - pole ID vrstev
 * @param konektor - typ konektoru (např. 'user_db')
 * @returns filtrované pole ID vrstev
 */
export const filterLayersByKonektor = (layersConfig: {[key: string]: LayerVO} | undefined, layerIds: number[], konektor: string): number[] => {
    if (!layersConfig || !layerIds) {
        return [];
    }

    return layerIds.filter(layerId => {
        const layerConfig = layersConfig[layerId.toString()];
        return layerConfig?.konektor === konektor;
    });
};

/**
 * Získání efektivního layerId (parent pokud existuje, jinak původní)
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID vrstvy jako číslo
 * @returns efektivní layerId
 */
export const getEffectiveLayerId = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): LayerId => {
    const parentLayerId = getParentLayerId(layersConfig, layerId);
    return parentLayerId ?? layerId;
};

/**
 * Kontrola zda je vrstva parent některé z user_db vrstev
 * @param layersConfig - konfigurace všech vrstev
 * @param userDbLayers - pole ID vrstev typu user_db
 * @param layerId - testované parent layerId
 * @returns true pokud je layerId parentem některé user_db vrstvy
 */
export const isParentOfUserDbLayers = (layersConfig: {[key: string]: LayerVO} | undefined, userDbLayers: LayerId[] | undefined, layerId: LayerId): boolean => {
    if (!layersConfig || !userDbLayers?.length) return false;

    return userDbLayers.some(userDbLayerId => {
        const parent = getParentLayerId(layersConfig, userDbLayerId);
        return parent === layerId;
    });
};

/**
 * Vrátí kandidátní ID popisných (label) vrstev pro danou datovou vrstvu.
 * Popisná vrstva má `sourceLayerId` ukazující na datovou vrstvu; u diskriminovaných vrstev
 * preferuje kandidáty se shodným `parentLayerId`. Používá katalog vrstev i SearchPanel filtr,
 * aby se popisky zapínaly/vypínaly společně s datovou vrstvou (stejně jako v CatalogLayerTreeList).
 * @param layersConfig - konfigurace všech vrstev
 * @param layerId - ID datové vrstvy
 * @returns pole ID popisných vrstev (může být prázdné)
 */
export const getLabelLayerIds = (layersConfig: {[key: string]: LayerVO} | undefined, layerId: LayerId): number[] => {
    if (!layersConfig) return [];
    const numId = Number(layerId);
    const sourceCfg = getLayerConfig(layersConfig, numId);
    const sourceParent = Number(sourceCfg?.parentLayerId);

    const candidates = Object.entries(layersConfig)
        .filter(([, cfg]) => Number(cfg.sourceLayerId) === numId)
        .map(([id]) => Number(id))
        .filter(id => Number.isFinite(id));

    if (!Number.isFinite(sourceParent)) {
        return candidates;
    }

    const sameParent = candidates.filter(id => Number(getLayerConfig(layersConfig, id)?.parentLayerId) === sourceParent);
    return sameParent.length ? sameParent : candidates;
};

/**
 * Resolver pro handleMove: pokud je aktuální vrstva parent pro user_db vrstvy a v řádku
 * je hodnota diskriminátoru odpovídající některému child layeru, vrátí ID toho child layeru.
 * Jinak vrátí defaultLayer (row.layerId nebo původní layerId).
 *
 * @param layersConfig - konfigurace všech vrstev
 * @param userDbLayers - seznam aktivních user_db vrstev
 * @param originalLayerId - původní layerId z kontextu (parent nebo user_db vrstva)
 * @param row - data řádku (obsahuje hodnoty atributů)
 * @param discriminatorAttribute - (volitelně) atribut, který obsahuje číselníky s názvy child vrstev
 */
export const resolveMoveLayerId = (
    layersConfig: {[key: string]: LayerVO} | undefined,
    userDbLayers: LayerId[] | undefined,
    originalLayerId: LayerId,
    row: any,
    discriminatorAttribute?: LayerAttributeVO,
): LayerId => {
    const defaultLayer = row?.layerId ?? originalLayerId;

    if (!discriminatorAttribute || !layersConfig) return defaultLayer;

    // Pokud je child layer znám přímo (tabulka filtrovaná na konkrétní child), použijeme ho rovnou
    if (discriminatorAttribute.layerId) return discriminatorAttribute.layerId;

    if (!isParentOfUserDbLayers(layersConfig, userDbLayers, originalLayerId)) return defaultLayer;

    if (!discriminatorAttribute.ciselniky) return defaultLayer;

    // Hodnota diskriminátoru může být id, kod, nazev nebo objekt — zkusíme všechny varianty
    const rawVal = row?.[discriminatorAttribute.nazev] ?? row?.properties?.[discriminatorAttribute.nazev];
    if (rawVal == null) return defaultLayer;

    const valAsString = String(typeof rawVal === 'object' ? (rawVal.id ?? rawVal.kod ?? rawVal.nazev ?? rawVal) : rawVal);
    const matched = discriminatorAttribute.ciselniky.find(c =>
        c.id == (rawVal?.id ?? rawVal) ||
        c.kod === (rawVal?.kod ?? valAsString) ||
        c.nazev === (rawVal?.nazev ?? valAsString),
    );
    const layerLabel = matched?.nazev;
    if (!layerLabel) return defaultLayer;

    // Najdeme child layer podle labelu
    const childLayer = (userDbLayers || []).find(childId => {
        const cfg = getLayerConfig(layersConfig, childId);
        return cfg?.label === layerLabel;
    });

    return childLayer ?? defaultLayer;
};
